import { getApiUrl, getDrawpostApiUrl } from "../config/api";
import { authFetch } from "../config/authFetch";
import type {
  AdminPlan,
  AdminPrice,
  CampaignOffer,
  ContentLimitAdminView,
  ContentLimitSettings,
  Coupon,
  PlanAssignment,
  PlanConfigRow,
  PlanOffer,
  ProductLine,
  UserCredits,
  UserSummary,
} from "../screens/Billing/types";

/**
 * Billing admin API. Subscription endpoints are reached through the DrawPost
 * backend's `/api/subscription` proxy (it forwards the admin Bearer token); the
 * product line is always sent explicitly because that proxy defaults to DrawPost.
 */

const subscription = (path: string) => getDrawpostApiUrl(`/api/subscription/v1.0.0${path}`);
const drawpost = (path: string) => getDrawpostApiUrl(`/api/v1.0.0${path}`);

/** Error carrying the server's message (already translated) for display. */
export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function requestJson<T>(url: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (init.body !== undefined) headers.set("Content-Type", "application/json");

  const response = await authFetch(url, { ...init, headers });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || result?.status !== "success") {
    throw new ApiError(result?.message || `Request failed (HTTP ${response.status})`, response.status);
  }
  return result.data as T;
}

const send = <T>(url: string, method: string, body?: unknown) =>
  requestJson<T>(url, { method, body: body === undefined ? undefined : JSON.stringify(body) });

// --- Plans & prices ---------------------------------------------------------

export const listPlans = (productLine: ProductLine) =>
  requestJson<AdminPlan[]>(subscription(`/admin/plans?productLine=${productLine}`));

export interface CreatePlanInput {
  name: string;
  description?: string;
  productLine: ProductLine;
  category: "starter" | "growth" | "creator" | "addon" | "free";
  accountType: "ALL" | "CLIENT" | "AGENCY";
  visibility: "PUBLIC" | "PRIVATE";
  trialDays?: number;
  prices: Array<{ billingPeriod: string; amount: number; currency: string; creditAmount?: number | null }>;
}

export const createPlan = (input: CreatePlanInput) => send<unknown>(subscription("/plans"), "POST", input);

export const updatePlan = (
  planId: number,
  input: Partial<Pick<AdminPlan, "name" | "description" | "isActive" | "visibility" | "accountType">> & {
    /** Applied to every recurring price; 0 = no trial. */
    trialDays?: number;
  }
) => send<AdminPlan>(subscription(`/admin/plans/${planId}`), "PATCH", input);

export const addPrice = (
  planId: number,
  input: { billingPeriod: string; amount: number; currency: string; creditAmount?: number | null; trialDays?: number | null }
) => send<AdminPrice>(subscription(`/admin/plans/${planId}/prices`), "POST", input);

export const setPriceActive = (priceId: number, isActive: boolean) =>
  send<AdminPrice>(subscription(`/admin/prices/${priceId}`), "PATCH", { isActive });

export const changePriceAmount = (priceId: number, amount: number) =>
  send<AdminPrice>(subscription("/price"), "PUT", { priceId, amount });

// --- DrawPost plan settings (limits) -----------------------------------------

export const getPlanConfig = (planId: number) => requestJson<PlanConfigRow[]>(drawpost(`/admin/plans/${planId}/config`));

export const savePlanConfig = (planId: number, configs: PlanConfigRow[]) =>
  send<{ updated: number }>(drawpost(`/admin/plans/${planId}/config`), "PUT", { configs });

export const deletePlanConfig = (planId: number, key: string) =>
  send<{ deleted: number }>(drawpost(`/admin/plans/${planId}/config/${encodeURIComponent(key)}`), "DELETE");

// --- Content limits (global, every user) --------------------------------------

export const getContentLimits = () => requestJson<ContentLimitAdminView>(drawpost("/admin/content-limits"));

export const saveContentLimits = (limits: ContentLimitSettings) =>
  send<ContentLimitAdminView>(drawpost("/admin/content-limits"), "PUT", limits);

// --- Plan assignments --------------------------------------------------------

export const listAssignments = (filter: { userId?: number; planId?: number } = {}) => {
  const query = new URLSearchParams();
  if (filter.userId) query.set("userId", String(filter.userId));
  if (filter.planId) query.set("planId", String(filter.planId));
  const qs = query.toString();
  const path = qs ? `/admin/plan-assignments?${qs}` : "/admin/plan-assignments";
  return requestJson<PlanAssignment[]>(subscription(path));
};

export const assignPlan = (input: { userId: number; planId: number; note?: string }) =>
  send<PlanAssignment>(subscription("/admin/plan-assignments"), "POST", input);

export const removeAssignment = (id: number) =>
  send<PlanAssignment>(subscription(`/admin/plan-assignments/${id}`), "DELETE");

// --- Discount offers ---------------------------------------------------------

export const listOffers = () => requestJson<PlanOffer[]>(subscription("/admin/offers"));

export type OfferInput = Partial<
  Omit<PlanOffer, "id" | "createdAt" | "updatedAt" | "_count" | "plan_offer_items">
> & {
  /** Each plan with its discount %; the server creates the discounted prices. */
  items?: Array<{ basePlanId: number; discountPercent: number; badge: string | null; sortOrder: number }>;
};

export const createOffer = (input: OfferInput) => send<PlanOffer>(subscription("/admin/offers"), "POST", input);

export const updateOffer = (id: number, input: OfferInput) =>
  send<PlanOffer>(subscription(`/admin/offers/${id}`), "PATCH", input);

// --- Coupons (DrawPost) -------------------------------------------------------

export const listCoupons = () => requestJson<Coupon[]>(subscription("/admin/coupons"));

export const createCoupon = (input: Record<string, unknown>) =>
  send<Coupon>(subscription("/admin/coupons"), "POST", input);

export const updateCoupon = (id: number, input: Record<string, unknown>) =>
  send<Coupon>(subscription(`/admin/coupons/${id}`), "PATCH", input);

// --- Free-days campaigns ------------------------------------------------------

export const listCampaignOffers = () => requestJson<CampaignOffer[]>(subscription("/admin/campaign-offers"));

export const createCampaignOffer = (input: {
  name: string;
  description?: string;
  freeDays: number;
  enrollUntil: string;
  isActive: boolean;
}) => send<unknown>(subscription("/campaign-offer"), "POST", input);

export const updateCampaignOffer = (id: number, input: Partial<Omit<CampaignOffer, "id" | "createdAt">>) =>
  send<CampaignOffer>(subscription(`/admin/campaign-offers/${id}`), "PATCH", input);

// --- Credits (DrawPost) ---------------------------------------------------------

export const grantCredits = (input: { userId: number; amount: number; note?: string }) =>
  send<{ ledgerEntryId: string; amount: number }>(drawpost("/admin/credits/grant"), "POST", input);

export const getUserCredits = (userId: number, page = 1) =>
  requestJson<UserCredits>(drawpost(`/admin/users/${userId}/credits?page=${page}&pageSize=20`));

// --- User lookup ----------------------------------------------------------------

export const searchUsers = async (search: string): Promise<UserSummary[]> => {
  const query = new URLSearchParams({ page: "1", pageSize: "8", sortBy: "createdAt", sortOrder: "desc", search });
  const data = await requestJson<{ users?: UserSummary[] }>(getApiUrl(`/api/auth/v1.0.0/users?${query.toString()}`));
  return (data.users ?? []).map((u) => ({ id: u.id, email: u.email, firstName: u.firstName, lastName: u.lastName }));
};
